import { Component, Input } from '@angular/core'
import { OrderPatch, OrderRead, OrdersService, StatusRead } from '@app/generated'
import { SelectComponent } from '@core/components/select/select.component'
import { NotificationService } from '@core/services/notification.service'
import { OptionComponent } from '@core/components/option/option.component'
import { FormFieldComponent } from '@core/components/form-field/form-field.component'
import { ButtonComponent } from '@core/components/button/button.component'
import { FormControl, FormGroup } from '@angular/forms'
import { ConfirmationComponent } from '@core/components/confirmation/confirmation.component'
import { ModalService } from '@core/services/modal.service'
import { finalize, forkJoin } from 'rxjs'
import { LoaderService } from '@core/services/loader.service'
import { getItemTranslation } from '@app/general/utils/get-item-translation'
import { CommonModule } from '@angular/common'
import { sortStatuses } from '@app/orders/utils'

@Component({
	standalone: true,
	selector: 'order-change-status-form',
	templateUrl: './change-status.form.html',
	styleUrls: ['./change-status.form.scss'],
	imports: [SelectComponent, OptionComponent, FormFieldComponent, ButtonComponent, ConfirmationComponent, CommonModule],
})
export class ChangeStatusComponent {
	@Input() order?: OrderRead
	@Input() ids?: string[]
	orders: OrderRead[] = []
	statuses: StatusRead[] = []
	orderPath: OrderPatch = {} as OrderPatch
	statusForm = {
		formGroup: new FormGroup({
			status: new FormControl<StatusRead | null>(null),
		}),
	}
	protected readonly getItemTranslation = getItemTranslation
	protected readonly confirmButtonText = $localize`Confirm`
	protected readonly cancelButtonText = $localize`Cancel`

	constructor(
		private ordersService: OrdersService,
		private notificationService: NotificationService,
		private modalService: ModalService,
		private loaderService: LoaderService,
	) {}

	ngOnInit() {
		this.fetchStatuses()
		if (this.order) {
			this.statusForm.formGroup.patchValue({
				status: this.order.status,
			})
		}
	}

	fetchStatuses() {
		this.ordersService.getStatuses('order').subscribe({
			next: (response: StatusRead[]) => {
				this.statuses = sortStatuses(response)
			},
			error: error => {
				let message = $localize`Was not able to fetch statuses`
				if (error.code === 422) {
					message = $localize`Form contains errors`
				}
				this.notificationService.error(message)
			},
		})
	}

	updateStatus() {
		this.orderPath = {
			status_id: this.statusForm.formGroup.controls['status'].value?.id,
		}

		this.patchData()
	}

	cancelUpdateStatus() {
		this.modalService.modalRef.close(false)
	}

	private patchData() {
		if (this.ids && this.ids.length > 0) {
			forkJoin(
				this.ids.map((id: string) => {
					return this.ordersService.patchOrder(id, this.orderPath)
				}),
			).subscribe({
				next: () => {
					this.ids!.length > 1
						? this.notificationService.success($localize`Order status has been updated`)
						: this.notificationService.success($localize`Orders status has been updated`)
					this.modalService.modalRef.close()
				},
				error: () => {
					this.ids!.length > 1
						? this.notificationService.error($localize`Was not able to update orders status`)
						: this.notificationService.error($localize`Was not able to update order status`)
				},
			})
		} else if (this.order?.id) {
			this.ordersService
				.patchOrder(this.order.id, this.orderPath)
				.pipe(finalize(() => this.loaderService.disable()))
				.subscribe({
					next: () => {
						const newValue = this.statuses.find(
							status => status.id === this.statusForm.formGroup.controls['status'].value?.id,
						)
						this.modalService.modalRef.close(newValue)
						this.notificationService.success($localize`Order status has been updated`)
					},
					error: err => {
						if (err.error.detail) {
							if (err.error.detail.type == 'not_allowed_permission')
								this.notificationService.error($localize`You have no permissions to change this status!`)
							else this.notificationService.error(err.error.detail.msg)
						} else this.notificationService.error($localize`Failed to patch order status`)
					},
				})
		}
	}
}
